import React from 'react';
import {
    selectCapsuleSymbolBindingFromState,
    showNiceDate,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';

import {
    CandlestickTileConfigState,
    initialCandlestickTileConfigState,
} from 'widgets/candlestickTile/store/config/CandlestickTileConfigState';

import {
    buildCandlestickTileData,
    CandlestickTileDataTimeSeriesItem,
} from 'widgets/candlestickTile/candlestickTileData';
import {
    PriceStreamState,
    PriceTimeDataTimeSeriesItem,
} from 'widgets/candlestickTile/store/stream/PriceStreamState';
import { toCandlestickRequestUid } from 'widgets/candlestickTile/api/CandlestickTileStreamRequest';

export interface WidgetPanelCandlestickTileProps
    extends WidgetPanelDetailProps {
    mode:
        | 'streamTimeSeries'
        | 'orderStat'
        | 'disableRange'
        | 'timeSeries'
        | 'data';
}

export default function WidgetPanelCandlestickTile(
    props: WidgetPanelCandlestickTileProps
) {
    const {
        widgetState,
        mode,
        widget: { id: widgetKey },
        state,
    } = props;

    const config =
        widgetState?.config ??
        (initialCandlestickTileConfigState as CandlestickTileConfigState);

    const symbolBinding = selectCapsuleSymbolBindingFromState(props);

    if (!symbolBinding) {
        return <></>;
    }

    const { symbol, source } = symbolBinding;

    const requestUid = toCandlestickRequestUid(symbol, config);
    const streamState: PriceStreamState | undefined =
        state.state.streams.data.priceTiles[requestUid];
    const candlestickTileData = buildCandlestickTileData(
        config.bucketSize,
        streamState?.data,
        config.showDataFor
    );

    if (candlestickTileData) {
        showNiceDate(candlestickTileData, 'startTime');
        showNiceDate(candlestickTileData, 'endTime');

        for (const t of candlestickTileData.timeSeries) {
            showNiceDate(t, 'startTime');
            showNiceDate(t, 'time');
            showNiceDate(t, 'endTime');
        }
    }

    for (const b of streamState?.data?.timeSeries ?? []) {
        const withTime = b as unknown as { time: number };
        withTime.time = b._i * 60000;
        showNiceDate(withTime, 'time');
    }

    if (mode === 'streamTimeSeries') {
        return streamState!.data!.timeSeries ? (
            <WidgetPanelGrid<PriceTimeDataTimeSeriesItem>
                data={streamState!.data!.timeSeries}
                getRowDataId={(d) => d._i.toString()}
                csvFileName={`${widgetKey}-streamTimeSeries`}
            />
        ) : (
            <></>
        );
    }

    if (mode === 'timeSeries') {
        return candlestickTileData!.timeSeries ? (
            <WidgetPanelGrid<CandlestickTileDataTimeSeriesItem>
                data={candlestickTileData!.timeSeries}
                getRowDataId={(d) => d._i.toString()}
                csvFileName={`${widgetKey}-timeSeries`}
            />
        ) : (
            <></>
        );
    }

    if (mode === 'orderStat') {
        return <JsonPanel data={streamState?.data?.orderStat} />;
    }

    if (mode === 'disableRange') {
        return <JsonPanel data={streamState?.data?.disableRange} />;
    }

    return (
        <div className="widget-panel">
            <div className="title">Symbol Capsule Data</div>
            <div className="field">
                <span className={'label'}>Symbol:</span>
                {symbol}
            </div>
            <div className="field">
                <span className={'label'}>Source</span>
                {source}
            </div>
            <div className="field">
                <span className="label">Request Uid:</span>
                {requestUid}
            </div>
            <JsonPanel data={candlestickTileData} />
        </div>
    );
}

export function addCandlestickTilePanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const render = () => (
        <WidgetPanelCandlestickTile {...detailProps} mode="streamTimeSeries" />
    );
    panes.push({
        menuItem: 'Stream Time Series',
        render,
    });

    const renderTs = () => (
        <WidgetPanelCandlestickTile {...detailProps} mode="timeSeries" />
    );
    panes.push({
        menuItem: 'Chart Time Series',
        render: renderTs,
    });

    const renderB = () => (
        <WidgetPanelCandlestickTile {...detailProps} mode="orderStat" />
    );
    panes.push({
        menuItem: 'OrderStat',
        render: renderB,
    });

    const renderDisableRange = () => (
        <WidgetPanelCandlestickTile {...detailProps} mode="disableRange" />
    );
    panes.push({
        menuItem: 'DisableRange',
        render: renderDisableRange,
    });

    const renderData = () => (
        <WidgetPanelCandlestickTile {...detailProps} mode="data" />
    );
    panes.push({
        menuItem: 'Data',
        render: renderData,
    });
}
